import React from 'lib/react';
import {
  EVCheckbox,
  EVGrid,
  EVTreeItem,
  EVTypography,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import clsx from 'clsx';
import { getHighlightedText, getAllOrgIds } from 'features/orderHistory/OrderHistory.utils';
import PropTypes from 'prop-types';
import OrgNodesFilterStyles from './OrgNodesFilterStyles';

function OrgNodes(props) {
  const {
    node, isEvenDepth, isFirst, selectedNodeIds, setSelectedNodeIds, trimmedSearchInput,
  } = props;

  const { orgId, orgName, children } = node;

  const classes = OrgNodesFilterStyles();
  const { t } = useTranslation();

  const handleOrgNodeSelect = ({ target: { value, checked } }) => {
    const id = Number(value);
    const set = new Set(selectedNodeIds);
    if (checked) set.add(id);
    else set.delete(id);
    setSelectedNodeIds([...set]);
  };

  const areAllChildrenSelected = (childrenList) => {
    const set = new Set(selectedNodeIds);
    const allChildrenIds = childrenList.flatMap((child) => getAllOrgIds(child));
    return allChildrenIds.every((id) => set.has(id));
  };

  const handleSelectAllChildren = (childrenList) => ({ target: { checked } }) => {
    const set = new Set(selectedNodeIds);
    const allChildrenIds = childrenList.flatMap((child) => getAllOrgIds(child));
    allChildrenIds.forEach((id) => (checked ? set.add(id) : set.delete(id)));
    setSelectedNodeIds([...set]);
  };

  return (
    <EVTreeItem
      key={orgId}
      nodeId={String(orgId)}
      classes={{
        group: classes.treeItemGroup,
        iconContainer: classes.treeItemIcon,
        label: classes.treeItemLabel,
        content: clsx(isFirst && classes.firstOrgNodeContent),
      }}
      label={(
        <>
          <EVCheckbox
            id={`select-org-node-${orgId}`}
            color="primary"
            checked={selectedNodeIds.includes(orgId)}
            value={orgId}
            onChange={handleOrgNodeSelect}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
          <EVTypography
            component="label"
            htmlFor={`select-org-node-${orgId}`}
            classes={{ root: classes.labelText }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {getHighlightedText(orgName, trimmedSearchInput)}
          </EVTypography>
        </>
    )}
    >
      {children && children.length > 0
        && (
          <EVGrid className={clsx(classes.expandedChildrenContainer, isEvenDepth && 'even', isFirst && 'first')}>
            {children.length > 1 && (
              <EVTreeItem
                nodeId={`select-all-child-org-nodes-${orgId}`}
                classes={{ group: classes.treeItemGroup, iconContainer: classes.treeItemSelectIcon, label: classes.treeItemSelectLabel }}
                label={(
                  <>
                    <EVCheckbox
                      id={`select-all-child-org-nodes-${orgId}`}
                      color="primary"
                      checked={areAllChildrenSelected(children)}
                      onChange={handleSelectAllChildren(children)}
                    />
                    <EVTypography
                      component="label"
                      htmlFor={`select-all-child-org-nodes-${orgId}`}
                      classes={{ root: classes.selectAllLabelText }}
                    >
                      {t('order_history__actions__select_all')}
                    </EVTypography>
                  </>
                )}
              />
            )}
            {children.map((child) => (
              <OrgNodes
                key={child.orgId}
                node={child}
                isEvenDepth={!isEvenDepth}
                selectedNodeIds={selectedNodeIds}
                setSelectedNodeIds={setSelectedNodeIds}
                trimmedSearchInput={trimmedSearchInput}
              />
            ))}
          </EVGrid>
        )}
    </EVTreeItem>
  );
}

OrgNodes.defaultProps = {
  node: {},
  isEvenDepth: false,
  isFirst: false,
  selectedNodeIds: [],
  setSelectedNodeIds: () => {},
  trimmedSearchInput: '',
};

OrgNodes.propTypes = {
  node: PropTypes.shape({
    orgId: PropTypes.number,
    orgName: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isEvenDepth: PropTypes.bool,
  isFirst: PropTypes.bool,
  selectedNodeIds: PropTypes.arrayOf(PropTypes.number),
  setSelectedNodeIds: PropTypes.func,
  trimmedSearchInput: PropTypes.string,
};

export default OrgNodes;
